import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowRight, KeyRound } from 'lucide-react';
import { useT } from '../studioI18n';
import { useStudioAuth } from '../StudioAuthContext';
import { studioApi, apiError } from '../studioApi';
import { Page, MotionButton } from '../components/Motion';

const Field = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-600 ml-1">{label}</span>
    <input className="st-input mt-1" data-testid={testId} {...props} />
  </label>
);

export default function AuthPage() {
  const { t, lang } = useT();
  const { login, register } = useStudioAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get('mode') === 'signup' ? 'signup' : 'login'); // login | signup | forgot
  const [f, setF] = useState({ name: '', email: '', password: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'forgot') {
        await studioApi.post('/auth/forgot-password', { email: f.email, public_base: window.location.origin });
        toast.success(t.forgotSent, { duration: 7000 }); setMode('login');
      } else {
        if (mode === 'signup') await register({ name: f.name, email: f.email, password: f.password, phone: f.phone || null, language: lang, public_base: window.location.origin });
        else await login(f.email, f.password);
        navigate('/project');
      }
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const demo = async () => {
    setBusy(true);
    try { await login('demo@studio.com', 'demo1234'); navigate('/project'); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const title = mode === 'signup' ? t.welcome : mode === 'forgot' ? t.forgotTitle : t.welcomeBack;
  const sub = mode === 'signup' ? t.authSub : mode === 'forgot' ? t.forgotSub : t.authSubLogin;
  return (
    <Page testId="studio-auth" className="max-w-md mx-auto px-4 sm:px-6 py-10 sm:py-16">
      <motion.div layout className="st-glass-strong rounded-[2rem] p-6 sm:p-8" data-testid="auth-modal">
        <img src={`${process.env.PUBLIC_URL || ''}/studio-logo.png`} alt="PM Studio" className="h-24 w-auto object-contain" data-testid="auth-logo-img" />
        <AnimatePresence mode="wait">
          <motion.div key={mode} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.25 }}>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-4" data-testid="auth-title">{title}</h1>
            <p className="text-sm text-slate-600 mt-1.5">{sub}</p>
          </motion.div>
        </AnimatePresence>
        <form onSubmit={submit} className="space-y-3 mt-6">
          {mode === 'signup' && <Field label={t.name} testId="auth-name-input" value={f.name} onChange={set('name')} autoComplete="name" required />}
          <Field label={t.email} testId="auth-email-input" type="email" value={f.email} onChange={set('email')} autoComplete="email" required />
          {mode !== 'forgot' && <Field label={t.password} testId="auth-password-input" type="password" value={f.password} onChange={set('password')} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} minLength={mode === 'signup' ? 6 : 1} required />}
          {mode === 'signup' && <Field label={t.phone} testId="auth-phone-input" type="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" />}
          <MotionButton type="submit" disabled={busy} className="w-full mt-2" data-testid="auth-submit-btn">
            {busy ? t.loading : mode === 'signup' ? t.signup : mode === 'forgot' ? t.sendLink : t.login} <ArrowRight className="h-5 w-5" />
          </MotionButton>
        </form>
        <div className="flex flex-col items-center gap-2 mt-5 text-sm">
          {mode === 'login' && <button type="button" onClick={() => setMode('forgot')} className="text-slate-500 hover:text-teal-800 text-xs font-semibold inline-flex items-center gap-1" data-testid="auth-forgot-link"><KeyRound className="h-3.5 w-3.5" /> {t.forgot}</button>}
          <button type="button" onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')} className="font-bold text-teal-800 hover:underline" data-testid="auth-toggle-mode">{mode === 'signup' ? t.haveAccount : t.noAccount}</button>
        </div>
        {mode !== 'forgot' && (
          <div className="mt-6 pt-5 border-t border-slate-200/70 text-center">
            <p className="text-[11px] text-slate-500 mb-2">{t.demoHint}</p>
            <MotionButton type="button" variant="ghost" onClick={demo} disabled={busy} className="h-11 text-sm" data-testid="demo-customer-login-btn">{t.demoLogin}</MotionButton>
          </div>
        )}
      </motion.div>
    </Page>
  );
}

export function ResetPage() {
  const { t } = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await studioApi.post('/auth/reset-password', { token: params.get('token'), password: pw }); toast.success(t.resetDone); navigate('/auth'); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Page testId="studio-reset" className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <form onSubmit={submit} className="st-glass-strong rounded-[2rem] p-6 sm:p-8 space-y-4">
        <h1 className="text-3xl font-extrabold text-slate-900">{t.resetTitle}</h1>
        <Field label={t.newPassword} testId="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} minLength={6} required />
        <MotionButton type="submit" disabled={busy} className="w-full" data-testid="reset-submit-btn">{t.resetBtn}</MotionButton>
      </form>
    </Page>
  );
}
